<script setup lang="ts">
import { nextTick, onMounted, reactive, ref } from 'vue'
import { ElLoading, ElMessage, ElMessageBox } from 'element-plus'
import type { DaikuanLoanRow } from '@/api/modules/daikuan'
import {
  daikuanAddMoney,
  daikuanChangeStatus,
  daikuanSyncPrincipalWallet,
  daikuanSaveBanks,
  daikuanSaveMoneyMonths,
  daikuanSendCustomSms,
  daikuanSendWithdrawalSms,
  daikuanSetWithdrawalCode,
  deleteDaikuanLoan,
  fetchDaikuanContract,
  fetchDaikuanLoans,
} from '@/api/modules/daikuan'
import { fetchSiteSettings } from '@/api/modules/system'
import { formatEpoch, formatMoneyInt } from '@/utils/format'
import { parseLoanStatusPresetsConfig } from '@/constants/loanStatusPresets'

type DkStatusPresetRow = {
  key: string
  title: string
  status: number
  statusText: string
  text: string
  smsSuggest: string
}

const statusPresetRows = ref<DkStatusPresetRow[]>([])

function setStatusPresetRowsFromConfig(raw: unknown) {
  statusPresetRows.value = parseLoanStatusPresetsConfig(raw).map((p, i) => ({
    key: p.id ? `cfg:${p.id}` : `cfg:${i}`,
    title: p.title,
    status: p.status,
    statusText: p.statusText,
    text: p.text,
    smsSuggest: p.smsSuggest,
  }))
}

const q = reactive({ phoneOrOrder: '', keywordName: '', page: 1, rows: 10 })
const loading = ref(false)
const tableRows = ref<DaikuanLoanRow[]>([])
const totalElements = ref(0)

async function reload() {
  loading.value = true
  try {
    const por = q.phoneOrOrder.trim()
    const nm = q.keywordName.trim()
    const { data } = await fetchDaikuanLoans({
      ...(por ? { phoneOrOrder: por } : {}),
      ...(nm ? { keywordName: nm } : {}),
      page: q.page,
      rows: q.rows,
    })
    tableRows.value = data.content ?? []
    totalElements.value = data.totalElements ?? 0
  } finally {
    loading.value = false
  }
}

function search() {
  q.page = 1
  reload()
}

async function reloadStatusPresets() {
  try {
    const { data } = await fetchSiteSettings()
    setStatusPresetRowsFromConfig(data.loan_status_presets)
  } catch {
    setStatusPresetRowsFromConfig('')
  }
}

onMounted(async () => {
  await Promise.all([reload(), reloadStatusPresets()])
})

const statusDlg = ref(false)
const moneyDlg = ref(false)
const moneyTermsDlg = ref(false)
const bankDlg = ref(false)
const smsDlg = ref(false)
const contractDlg = ref(false)

const htmlBody = ref('')
const current = ref<DaikuanLoanRow | null>(null)

/** Off-screen DOM for PNG export (must not be `display:none` or height collapses). */
const contractCaptureHost = ref<HTMLElement | null>(null)
const captureHtml = ref('')
const contractDialogBody = ref<HTMLElement | null>(null)
/** Row `id` while generating contract PNG from list action. */
const contractPngRowId = ref<number | null>(null)

const statusForm = reactive({
  status: 0,
  statusText: '',
  promptText: '',
  smsContent: '',
  sendSms: false,
  forceSmsbao: false,
  presetKey: '' as string,
})

const moneyForm = reactive({ amount: '' })
const moneyTermsForm = reactive({ money: '', months: '' })
const bankForm = reactive({ bank: '', banknum: '' })
const smsForm = reactive({ content: '' })

function fmtMoney(n?: number | null) {
  return formatMoneyInt(n)
}

function clientLabel(row: DaikuanLoanRow) {
  const nm = row.customerName?.trim()
  const ph = row.user?.trim() || ''
  return nm ? `${nm} (${ph})` : ph || '—'
}

function openStatus(row: DaikuanLoanRow) {
  current.value = row
  statusForm.status = row.status ?? 0
  statusForm.statusText = row.statusText || ''
  statusForm.promptText = row.text || ''
  statusForm.smsContent = ''
  statusForm.sendSms = false
  statusForm.forceSmsbao = false
  statusForm.presetKey = ''
  statusDlg.value = true
}

function applyPresetByKey(val: string | null | undefined) {
  const k = val == null || val === '' ? '' : String(val)
  statusForm.presetKey = k
  if (!k)
    return
  const p = statusPresetRows.value.find((x) => x.key === k)
  if (!p)
    return
  statusForm.status = p.status
  statusForm.statusText = p.statusText
  statusForm.promptText = p.text
  statusForm.smsContent = p.smsSuggest
}

function openStatusTemplateSettings() {
  window.open('/system/settings#loan-status-presets', '_blank', 'noopener')
}

async function submitStatus() {
  if (!current.value) return
  const sms = statusForm.smsContent.trim()
  if ((statusForm.sendSms || statusForm.forceSmsbao) && !sms) {
    ElMessage.warning('发送短信或强制 smsbao 时请填写短信内容')
    return
  }
  const doSend = statusForm.sendSms || statusForm.forceSmsbao
  try {
    const { data } = await daikuanChangeStatus({
      id: current.value.id,
      status: Number(statusForm.status),
      ...(statusForm.statusText ? { statusText: statusForm.statusText } : {}),
      ...(doSend && sms ? { smsContent: sms } : {}),
      text: statusForm.promptText,
      forceSmsbao: doSend && !!sms,
    })
    if (data?.status === 1) ElMessage.success(data.msg ?? '已保存')
    else ElMessage.error(String(data?.msg ?? '提交失败'))
    statusDlg.value = false
    reload()
  } catch {
    /* interceptor */
  }
}

async function onSyncPrincipalWallet(row: DaikuanLoanRow) {
  const id = row.id
  try {
    await ElMessageBox.confirm(
      `将订单 ${row.orderNum ?? id} 的本金写入用户钱包 user.money（覆盖，相当于旧版"审核并授信"单笔本金）。建议在资料审核后操作。`,
      '授信本金到钱包',
      { type: 'warning', confirmButtonText: '确认', cancelButtonText: '取消' },
    )
    const { data } = await daikuanSyncPrincipalWallet(id)
    if (Number(data?.status) === 1) ElMessage.success(data.msg ?? '已写入钱包')
    else ElMessage.error(String(data?.msg ?? '失败'))
    reload()
  } catch (e) {
    if (e !== 'cancel') {
      /* */
    }
  }
}

function openWallet(row: DaikuanLoanRow) {
  current.value = row
  moneyForm.amount = ''
  moneyDlg.value = true
}

async function submitWallet() {
  if (!current.value) return
  const m = Number(moneyForm.amount)
  if (!Number.isFinite(m) || m === 0) {
    ElMessage.warning('请输入有效金额：正数为加，负数为扣，不能为 0')
    return
  }
  try {
    const { data } = await daikuanAddMoney(current.value.id, m)
    if (data?.status === 1) ElMessage.success(data.msg ?? '钱包已更新')
    else ElMessage.error(String(data?.msg ?? '失败'))
    moneyDlg.value = false
    reload()
  } catch {
    /* */
  }
}

function openMoneyTerms(row: DaikuanLoanRow) {
  current.value = row
  moneyTermsForm.money = row.money != null ? String(row.money) : ''
  moneyTermsForm.months = row.months != null ? String(row.months) : ''
  moneyTermsDlg.value = true
}

async function submitMoneyTerms() {
  if (!current.value) return
  const money = Number(moneyTermsForm.money)
  const months = Number(moneyTermsForm.months)
  if (!Number.isFinite(money) || money <= 0 || !Number.isFinite(months) || months <= 0) {
    ElMessage.warning('金额和期数必须为正数（受站点配置限制/档位约束）')
    return
  }
  try {
    const { data } = await daikuanSaveMoneyMonths(current.value.id, money, months)
    if (data?.status === 1) ElMessage.success(data.msg ?? '已保存')
    else ElMessage.error(String(data?.msg ?? '失败'))
    moneyTermsDlg.value = false
    reload()
  } catch {
    /* */
  }
}

function openBank(row: DaikuanLoanRow) {
  current.value = row
  bankForm.bank = row.bank || ''
  bankForm.banknum = row.banknum || ''
  bankDlg.value = true
}

async function submitBank() {
  if (!current.value?.user) {
    ElMessage.warning('该行缺少用户手机号')
    return
  }
  if (!bankForm.bank.trim() || !bankForm.banknum.trim()) {
    ElMessage.warning('请填写银行名称和卡号')
    return
  }
  try {
    const { data } = await daikuanSaveBanks(current.value.user, bankForm.bank.trim(), bankForm.banknum.trim())
    if (data?.status === 1) ElMessage.success(data.msg ?? '已保存')
    else ElMessage.error(String(data?.msg ?? '失败'))
    bankDlg.value = false
    reload()
  } catch {
    /* */
  }
}

function showBank(row: DaikuanLoanRow) {
  ElMessageBox.alert(
    `收款银行：${row.bank || '—'}<br/>银行卡号：${row.banknum || '—'}`,
    '收款信息',
    { dangerouslyUseHTMLString: true },
  )
}

function openSendSms(row: DaikuanLoanRow) {
  current.value = row
  smsForm.content =
    (row.text && row.text.trim()) ||
    (row.statusText && row.statusText.trim()) ||
    `您的订单 ${row.orderNum ?? ''} 状态已更新。`
  smsDlg.value = true
}

async function submitSms() {
  if (!current.value?.user) {
    ElMessage.warning('无用户手机号')
    return
  }
  const c = smsForm.content.trim()
  if (!c) {
    ElMessage.warning('短信内容不能为空')
    return
  }
  try {
    await daikuanSendCustomSms(current.value.user, c)
    ElMessage.success('已提交发送（Smsbao）')
    smsDlg.value = false
  } catch {
    /* */
  }
}

async function onChangeWithdrawalCode(row: DaikuanLoanRow) {
  try {
    const { value } = await ElMessageBox.prompt(
      '新取款码（6-10 位数字，保存到该订单用户的 user.discode）',
      `取款码 · 订单 ${row.orderNum ?? row.id}`,
      {
        confirmButtonText: '确认',
        cancelButtonText: '取消',
        inputValue: row.discode != null ? String(row.discode) : '',
        inputValidator: (v) => {
          if (!v || !/^\d{6,10}$/.test(v)) return '必须是 6-10 位数字'
          return true
        },
      },
    )
    const { data } = await daikuanSetWithdrawalCode(row.id, value)
    if (Number(data?.status) === 1) {
      ElMessage.success(data.msg ?? '取款码已更新')
      reload()
    } else {
      ElMessage.error(String(data?.msg ?? '更新失败'))
    }
  } catch (e) {
    if (e !== 'cancel') {
      /* */
    }
  }
}

async function onSendWithdrawalSms(row: DaikuanLoanRow) {
  if (row.discode == null) {
    ElMessage.warning('请先设置取款码')
    return
  }
  try {
    await ElMessageBox.confirm(
      `向 ${row.user ?? '该用户'} 发送取款码短信？短信内容会包含当前取款码。`,
      '发送取款码短信',
      { type: 'info' },
    )
    const { data } = await daikuanSendWithdrawalSms(row.id)
    if (Number(data?.status) === 1) ElMessage.success('已请求发送（Smsbao）')
    else ElMessage.error(String(data?.msg ?? '发送失败'))
  } catch (e) {
    if (e !== 'cancel') {
      /* */
    }
  }
}

function safeContractFileBase(row: DaikuanLoanRow) {
  const raw = String(row.orderNum ?? row.id ?? 'contract').trim()
  return raw.replace(/[/\\?%*:|"<>]/g, '_').slice(0, 120) || 'contract'
}

async function waitForImages(root: HTMLElement) {
  const imgs = root.querySelectorAll('img')
  await Promise.all(
    [...imgs].map(
      async (img) => {
        if (img.complete && img.naturalWidth > 0) {
          try {
            await img.decode()
          } catch {
            /* ignore */
          }
          return
        }
        await new Promise<void>((resolve) => {
          img.addEventListener('load', () => resolve(), { once: true })
          img.addEventListener('error', () => resolve(), { once: true })
          window.setTimeout(() => resolve(), 6000)
        })
        try {
          await img.decode()
        } catch {
          /* ignore */
        }
      },
    ),
  )
}

/** PNG output is always 1000×1200; content is scaled to fit (no clipping). */
const CONTRACT_EXPORT_WIDTH_PX = 1000
const CONTRACT_EXPORT_HEIGHT_PX = 1200
const CONTRACT_EXPORT_FONT_PX = 17
const CONTRACT_EXPORT_PAD_BOTTOM_PX = 32
/** White border inset on the final 1000×1200 PNG. */
const CONTRACT_EXPORT_EDGE_MARGIN_PX = 48
/** Capture at 2× then downscale for sharper text and seals. */
const CONTRACT_EXPORT_CAPTURE_SCALE = 2

function applyContractExportTypography(root: HTMLElement) {
  const fs = `${CONTRACT_EXPORT_FONT_PX}px`
  const lh = '1.38'
  root.style.fontSize = fs
  root.style.lineHeight = lh
  root.style.color = '#1a2f4d'
  const textish = 'article, .contract-a4-article, article p, article li, article ol, p, li, span'
  root.querySelectorAll(textish).forEach((node) => {
    const el = node as HTMLElement
    if (el.closest('.contract-sig-handwriting'))
      return
    el.style.fontSize = fs
    el.style.lineHeight = lh
  })
  root.querySelectorAll('article strong, article u, p strong').forEach((node) => {
    const el = node as HTMLElement
    el.style.fontSize = fs
  })
  root.querySelectorAll('article p').forEach((node, idx) => {
    const el = node as HTMLElement
    if (idx === 0 && el.textContent?.toUpperCase().includes('LOAN AGREEMENT')) {
      el.style.fontSize = '20px'
      el.style.lineHeight = '1.3'
    }
  })
}

function applyContractA4PageStyles(root: HTMLElement, pageHeight: number) {
  root.style.width = `${CONTRACT_EXPORT_WIDTH_PX}px`
  root.style.maxWidth = `${CONTRACT_EXPORT_WIDTH_PX}px`
  root.style.minHeight = `${pageHeight}px`
  root.style.height = `${pageHeight}px`
  root.style.boxSizing = 'border-box'
  root.style.overflow = 'visible'
  root.style.background = '#ffffff'
  root.style.paddingBottom = `${CONTRACT_EXPORT_PAD_BOTTOM_PX}px`
  root.style.setProperty('-webkit-font-smoothing', 'antialiased')
  root.style.textRendering = 'geometricPrecision'
  root.querySelectorAll('article, .contract-a4-article').forEach((node) => {
    const a = node as HTMLElement
    a.style.maxWidth = '100%'
    a.style.width = '100%'
    a.style.boxSizing = 'border-box'
    a.style.wordWrap = 'break-word'
  })
}

function applyContractSigLayoutStyles(root: HTMLElement, pageHeight: number) {
  applyContractA4PageStyles(root, pageHeight)
  applyContractExportTypography(root)
  root.querySelectorAll('.contract-sig-stack').forEach((node) => {
    const el = node as HTMLElement
    el.style.position = 'relative'
    el.style.display = 'block'
    el.style.minHeight = '0'
    el.style.lineHeight = '0'
    el.style.maxWidth = '100%'
  })
  root.querySelectorAll('.contract-sig-seal-wrap').forEach((node) => {
    const el = node as HTMLElement
    el.style.position = 'absolute'
    el.style.left = '50%'
    el.style.top = '72%'
    el.style.transform = 'translate(-50%, -50%)'
    el.style.marginTop = '0'
    el.style.width = 'min(360px, 75%)'
    el.style.maxWidth = '100%'
    el.style.display = 'block'
  })
  root.querySelectorAll('.contract-sig-seal-overlay').forEach((node) => {
    const el = node as HTMLElement
    el.style.position = 'static'
    el.style.width = '100%'
    el.style.margin = '0'
  })
  root.querySelectorAll('div[style*="min-height"]').forEach((node) => {
    const el = node as HTMLElement
    const mh = el.style.minHeight
    if (mh && (mh === '160px' || Number.parseInt(mh, 10) >= 120))
      el.style.minHeight = '0'
  })
  root.querySelectorAll('.contract-sig-handwriting img').forEach((node) => {
    const img = node as HTMLImageElement
    img.style.maxWidth = '100%'
    img.style.maxHeight = '200px'
    img.style.width = 'auto'
    img.style.height = 'auto'
  })
  root.querySelectorAll('img').forEach((node) => {
    const img = node as HTMLImageElement
    if (img.closest('.contract-sig-handwriting'))
      return
    img.style.maxWidth = '100%'
    img.style.height = 'auto'
  })
}

function measureContractExportHeight(root: HTMLElement): number {
  applyContractSigLayoutStyles(root, 32_000)
  root.style.height = 'auto'
  root.style.minHeight = '0'
  const inner = root.querySelector('article, .contract-a4-article') as HTMLElement | null
  if (inner) {
    inner.style.transform = 'none'
    inner.style.width = '100%'
  }
  const raw = Math.max(root.scrollHeight, root.offsetHeight)
  return Math.ceil(raw + CONTRACT_EXPORT_PAD_BOTTOM_PX)
}

/** Scale captured content into fixed 1000×1200 with edge margins (never crop). */
function fitCanvasToExportSize(src: HTMLCanvasElement): HTMLCanvasElement {
  const out = document.createElement('canvas')
  out.width = CONTRACT_EXPORT_WIDTH_PX
  out.height = CONTRACT_EXPORT_HEIGHT_PX
  const ctx = out.getContext('2d')
  if (!ctx)
    return src
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, out.width, out.height)
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  const m = CONTRACT_EXPORT_EDGE_MARGIN_PX
  const availW = out.width - m * 2
  const availH = out.height - m * 2
  const scale = Math.min(availW / src.width, availH / src.height)
  const dw = Math.round(src.width * scale)
  const dh = Math.round(src.height * scale)
  const x = Math.round(m + (availW - dw) / 2)
  const y = Math.round(m + (availH - dh) / 2)
  ctx.drawImage(src, x, y, dw, dh)
  return out
}

async function renderElementToPngFile(el: HTMLElement, filename: string) {
  const html2canvas = (await import('html2canvas')).default
  await new Promise<void>((r) => requestAnimationFrame(() => r()))
  const pageHeight = measureContractExportHeight(el)
  applyContractSigLayoutStyles(el, pageHeight)
  await new Promise<void>((r) => requestAnimationFrame(() => r()))
  const captured = await html2canvas(el, {
    scale: CONTRACT_EXPORT_CAPTURE_SCALE,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
    width: CONTRACT_EXPORT_WIDTH_PX,
    height: pageHeight,
    scrollX: 0,
    scrollY: 0,
    onclone: (_doc, cloned) => {
      applyContractSigLayoutStyles(cloned as HTMLElement, pageHeight)
    },
  })
  const canvas = fitCanvasToExportSize(captured)
  const url = canvas.toDataURL('image/png')
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

async function openContract(row: DaikuanLoanRow) {
  current.value = row
  try {
    const { data } = await fetchDaikuanContract(row.id)
    htmlBody.value = data.html || ''
    contractDlg.value = true
  } catch {
    htmlBody.value = ''
  }
}

async function downloadContractPng(row: DaikuanLoanRow) {
  if (contractPngRowId.value != null)
    return
  contractPngRowId.value = row.id
  const loading = ElLoading.service({ fullscreen: true, text: '正在生成合同图片…' })
  try {
    const { data } = await fetchDaikuanContract(row.id)
    const html = data.html || ''
    if (!html.trim()) {
      ElMessage.warning('合同内容为空')
      return
    }
    captureHtml.value = html
    await nextTick()
    await nextTick()
    const host = contractCaptureHost.value
    if (!host) {
      ElMessage.error('渲染容器未就绪')
      return
    }
    await waitForImages(host)
    await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())))
    await renderElementToPngFile(host, `loan-contract-${safeContractFileBase(row)}.png`)
    ElMessage.success('PNG 下载已开始')
  } catch {
    ElMessage.error('生成合同图片失败')
  } finally {
    captureHtml.value = ''
    contractPngRowId.value = null
    loading.close()
  }
}

async function downloadDialogContractPng() {
  if (!htmlBody.value.trim()) {
    ElMessage.warning('无内容可导出')
    return
  }
  const loading = ElLoading.service({ fullscreen: true, text: '正在生成合同图片…' })
  try {
    captureHtml.value = htmlBody.value
    await nextTick()
    await nextTick()
    const host = contractCaptureHost.value
    if (!host) {
      ElMessage.error('渲染容器未就绪')
      return
    }
    await waitForImages(host)
    await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())))
    const row = current.value
    const base = row ? safeContractFileBase(row) : 'contract'
    await renderElementToPngFile(host, `loan-contract-${base}.png`)
    ElMessage.success('PNG 下载已开始')
  } catch {
    ElMessage.error('生成合同图片失败')
  } finally {
    captureHtml.value = ''
    loading.close()
  }
}

async function onDelete(row: DaikuanLoanRow) {
  try {
    await ElMessageBox.confirm(
      `将订单 ${row.orderNum ?? row.id} 移入回收站？\n不会删除用户、提现记录和还款数据。可从「订单回收站」或用户的「修复订单」恢复。`,
      '移入回收站',
      { type: 'warning' },
    )
    await deleteDaikuanLoan(row.id)
    ElMessage.success('已移入回收站')
    reload()
  } catch (e) {
    if (e !== 'cancel') {
      /* */
    }
  }
}

function openYz(row: DaikuanLoanRow) {
  const u = row.yzpzImg
  if (!u) {
    ElMessage.info('无审核费凭证图片')
    return
  }
  window.open(u, '_blank', 'noopener')
}

function handleSizeChange(sz: number) {
  q.rows = sz
  q.page = 1
  reload()
}

function handlePageChange() {
  reload()
}
</script>

<template>
  <PageChrome
    title="贷款订单"
    description="手机号或订单号（同一框模糊）；客户姓名仍可单独筛选。状态快捷模板在系统设置中统一维护。钱包支持加/扣（负额），扣后余额≥0。"
  >
    <div class="ep-filter-panel">
      <el-form inline :model="q" label-width="88px">
        <el-form-item label="手机号/单号">
          <el-input
            v-model="q.phoneOrOrder"
            clearable
            placeholder="模糊：手机号或信贷订单号"
            style="width: 248px"
            @keyup.enter="search"
          />
        </el-form-item>
        <el-form-item label="客户姓名">
          <el-input
            v-model="q.keywordName"
            clearable
            placeholder="资料姓名（userinfo）模糊"
            style="width: 200px"
            @keyup.enter="search"
          />
        </el-form-item>
        <el-form-item>
          <el-space>
            <el-button type="primary" @click="search">查询</el-button>
            <el-button :loading="loading" @click="reload">刷新</el-button>
          </el-space>
        </el-form-item>
      </el-form>
    </div>

    <el-card shadow="hover" class="ep-table-scope dk-card">
      <el-table v-loading="loading" :data="tableRows" stripe border empty-text="暂无数据" size="default">
        <el-table-column prop="id" label="ID" width="72" align="center" />
        <el-table-column prop="orderNum" label="订单号" min-width="132" show-overflow-tooltip />
        <el-table-column label="客户" min-width="168" show-overflow-tooltip>
          <template #default="{ row }">{{ clientLabel(row as DaikuanLoanRow) }}</template>
        </el-table-column>
        <el-table-column label="金额" width="108" align="right">
          <template #default="{ row }">{{ fmtMoney((row as DaikuanLoanRow).money) }}</template>
        </el-table-column>
        <el-table-column label="期数" width="76" align="center">
          <template #default="{ row }">
            {{ (row as DaikuanLoanRow).months != null ? `${(row as DaikuanLoanRow).months} 期` : '—' }}
          </template>
        </el-table-column>
        <el-table-column label="创建时间" width="168" align="center">
          <template #default="{ row }">{{ formatEpoch((row as DaikuanLoanRow).addtime ?? undefined) }}</template>
        </el-table-column>
        <el-table-column label="状态" min-width="120" align="center">
          <template #default="{ row }">
            <div class="st-cell">
              <div class="st-main">{{ (row as DaikuanLoanRow).statusDisplayEn || '—' }}</div>
              <div
                v-if="
                  (row as DaikuanLoanRow).statusText &&
                  (row as DaikuanLoanRow).statusText !== (row as DaikuanLoanRow).statusDisplayEn
                "
                class="st-sub"
              >
                {{ (row as DaikuanLoanRow).statusText }}
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="短信" width="120" align="center">
          <template #default="{ row }">
            <el-button type="primary" link @click="openSendSms(row as DaikuanLoanRow)">发送状态</el-button>
          </template>
        </el-table-column>
        <el-table-column label="取款码" width="112" align="center">
          <template #default="{ row }">
            {{ (row as DaikuanLoanRow).discode != null ? (row as DaikuanLoanRow).discode : '—' }}
          </template>
        </el-table-column>
        <el-table-column label="钱包" width="108" align="right">
          <template #default="{ row }">{{ fmtMoney((row as DaikuanLoanRow).walletBalance) }}</template>
        </el-table-column>
        <el-table-column fixed="right" label="操作" width="520">
          <template #default="{ row }">
            <el-button link type="primary" @click="openStatus(row as DaikuanLoanRow)">修改状态</el-button>
            <el-button link type="primary" @click="onChangeWithdrawalCode(row as DaikuanLoanRow)">取款码</el-button>
            <el-button link type="primary" @click="onSendWithdrawalSms(row as DaikuanLoanRow)">发取款码</el-button>
            <el-button link type="primary" @click="showBank(row as DaikuanLoanRow)">收款信息</el-button>
            <el-button link type="primary" @click="openMoneyTerms(row as DaikuanLoanRow)">编辑金额/期数</el-button>
            <el-button link type="primary" @click="openBank(row as DaikuanLoanRow)">编辑银行卡</el-button>
            <el-button link type="primary" @click="openWallet(row as DaikuanLoanRow)">钱包加/扣</el-button>
            <el-button link type="success" @click="onSyncPrincipalWallet(row as DaikuanLoanRow)">授信本金</el-button>
            <el-button link type="warning" @click="openContract(row as DaikuanLoanRow)">合同</el-button>
            <el-button
              link
              type="warning"
              :loading="contractPngRowId === (row as DaikuanLoanRow).id"
              @click="downloadContractPng(row as DaikuanLoanRow)"
            >
              合同图片
            </el-button>
            <el-button link type="info" @click="openYz(row as DaikuanLoanRow)">凭证</el-button>
            <el-button link type="danger" @click="onDelete(row as DaikuanLoanRow)">回收站</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="ep-pager-footer pager">
        <el-pagination
          v-model:current-page="q.page"
          v-model:page-size="q.rows"
          :page-sizes="[7, 10, 20, 50]"
          layout="prev, pager, next, sizes, total"
          :total="totalElements"
          background
          @current-change="handlePageChange"
          @size-change="handleSizeChange"
        />
      </div>
    </el-card>

    <el-dialog v-model="statusDlg" title="修改订单状态" width="640px" destroy-on-close>
      <el-form v-if="current" label-width="120px">
        <el-form-item label="订单号">{{ current.orderNum }} / id {{ current.id }}</el-form-item>
        <el-form-item label="快捷模板">
          <el-space wrap alignment="flex-start">
            <el-select
              v-model="statusForm.presetKey"
              clearable
              placeholder="选择系统设置里的状态模板"
              filterable
              style="width: 320px"
              @change="applyPresetByKey"
            >
              <el-option v-for="p in statusPresetRows" :key="p.key" :label="p.title" :value="p.key" />
            </el-select>
            <el-button @click="openStatusTemplateSettings">管理模板…</el-button>
            <el-button :loading="loading" @click="reloadStatusPresets">重载模板</el-button>
          </el-space>
        </el-form-item>
        <el-form-item label="状态码" required>
          <el-input-number v-model="statusForm.status" :step="1" controls-position="right" style="width: 100%" />
        </el-form-item>
        <el-form-item label="状态文本">
          <el-input v-model="statusForm.statusText" type="textarea" :rows="2" />
        </el-form-item>
        <el-form-item label="提示文本">
          <el-input v-model="statusForm.promptText" type="textarea" :rows="3" placeholder="存入 order.text 字段" />
        </el-form-item>
        <el-form-item label="发送短信">
          <el-switch v-model="statusForm.sendSms" />
          <span class="hint">勾选且有内容时，提交会走 Smsbao 发送</span>
        </el-form-item>
        <el-form-item label="短信内容">
          <el-input v-model="statusForm.smsContent" type="textarea" :rows="2" />
        </el-form-item>
        <el-form-item label="强制 smsbao">
          <el-switch v-model="statusForm.forceSmsbao" />
          <span class="hint">配合后端自动下发；短信正文仍需填写</span>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="statusDlg = false">取消</el-button>
        <el-button type="primary" @click="submitStatus">提交</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="moneyDlg" title="钱包加 / 扣" width="480px" destroy-on-close>
      <template v-if="current">
        <p class="muted">
          用户 {{ current.user }} · 当前 ~{{ fmtMoney(current.walletBalance) }}（正数=加，负数=扣；扣后余额≥0）
        </p>
        <el-input v-model="moneyForm.amount" placeholder="例如 1000 或 -500" />
      </template>
      <template #footer>
        <el-button @click="moneyDlg = false">取消</el-button>
        <el-button type="primary" @click="submitWallet">提交</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="moneyTermsDlg" title="编辑借款金额/期数" width="500px" destroy-on-close>
      <el-form label-width="100px">
        <el-form-item label="贷款 ID">{{ current?.id }}</el-form-item>
        <el-form-item label="金额"><el-input v-model="moneyTermsForm.money" /></el-form-item>
        <el-form-item label="期数"><el-input v-model="moneyTermsForm.months" /></el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="moneyTermsDlg = false">取消</el-button>
        <el-button type="primary" @click="submitMoneyTerms">提交</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="bankDlg" title="编辑银行卡" width="500px" destroy-on-close>
      <el-form label-width="112px">
        <el-form-item label="银行"><el-input v-model="bankForm.bank" /></el-form-item>
        <el-form-item label="卡号"><el-input v-model="bankForm.banknum" /></el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="bankDlg = false">取消</el-button>
        <el-button type="primary" @click="submitBank">提交</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="smsDlg" title="发送当前状态（Smsbao）" width="520px" destroy-on-close>
      <el-input v-model="smsForm.content" type="textarea" :rows="5" />
      <template #footer>
        <el-button @click="smsDlg = false">取消</el-button>
        <el-button type="primary" @click="submitSms">发送</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="contractDlg" title="合同 HTML" width="900px" destroy-on-close>
      <el-alert title="内容由后端拼接。注意安全范围。" type="info" />
      <div ref="contractDialogBody" class="html-body" v-html="htmlBody" />
      <template #footer>
        <el-button @click="contractDlg = false">关闭</el-button>
        <el-button type="primary" @click="downloadDialogContractPng">下载 PNG</el-button>
      </template>
    </el-dialog>

    <!-- Off-screen: html2canvas needs laid-out box (not display:none). -->
    <div
      ref="contractCaptureHost"
      class="contract-capture-host"
      aria-hidden="true"
      v-html="captureHtml"
    />
  </PageChrome>
</template>

<style scoped lang="scss">
.dk-card {
  border-radius: var(--ep-radius-lg) !important;
}
.pager {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}
.st-cell {
  text-align: center;
}
.st-main {
  color: #2563eb;
  font-weight: 600;
  line-height: 1.35;
}
.st-sub {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
}
.html-body {
  max-height: 70vh;
  overflow: auto;
  margin: 12px auto 0;
  padding: 16px;
  max-width: 1000px;
  width: 100%;
  box-sizing: border-box;
  border: 1px solid var(--el-border-color-lighter);
  background: #fff;
  color: #1a2f4d;
  font-size: 14px;
  line-height: 1.45;
}

.html-body :deep(.contract-sig-stack) {
  position: relative !important;
  display: block !important;
  min-height: 0 !important;
  line-height: 0 !important;
}

.html-body :deep(.contract-sig-seal-wrap) {
  position: absolute !important;
  left: 50% !important;
  top: 72% !important;
  transform: translate(-50%, -50%) !important;
  width: min(360px, 75%) !important;
  margin: 0 !important;
}

.html-body :deep(.contract-sig-handwriting img) {
  max-width: 100% !important;
  max-height: 200px !important;
  width: auto !important;
  height: auto !important;
}

.html-body :deep(.contract-sig-seal-overlay) {
  width: 100% !important;
  margin: 0 !important;
}

.contract-capture-host {
  position: fixed;
  left: -12000px;
  top: 0;
  width: 1000px;
  max-width: 1000px;
  min-height: 0;
  height: auto;
  box-sizing: border-box;
  padding: 24px 40px 48px;
  background: #fff;
  color: #1a2f4d;
  font-size: 17px;
  line-height: 1.38;
  z-index: -10;
  pointer-events: none;
  overflow: visible;
  word-wrap: break-word;
}

.contract-capture-host :deep(article),
.contract-capture-host :deep(.contract-a4-article) {
  max-width: 100% !important;
  width: 100% !important;
  box-sizing: border-box !important;
  font-size: 17px !important;
  line-height: 1.38 !important;
}

.contract-capture-host :deep(article p),
.contract-capture-host :deep(article li) {
  font-size: 17px !important;
  line-height: 1.38 !important;
}

.contract-capture-host :deep(article p:first-of-type) {
  font-size: 20px !important;
}

.contract-capture-host :deep(article > p:last-child) {
  margin-top: 8px !important;
  padding-bottom: 4px !important;
}

.contract-capture-host :deep(.contract-sig-stack) {
  position: relative !important;
  display: block !important;
  min-height: 0 !important;
  line-height: 0 !important;
}

.contract-capture-host :deep(.contract-sig-seal-wrap) {
  position: absolute !important;
  left: 50% !important;
  top: 72% !important;
  transform: translate(-50%, -50%) !important;
  width: min(360px, 75%) !important;
  max-width: 100% !important;
  margin: 0 !important;
  display: block !important;
}

.contract-capture-host :deep(.contract-sig-handwriting img) {
  max-width: 100% !important;
  max-height: 200px !important;
  width: auto !important;
  height: auto !important;
}

.contract-capture-host :deep(.contract-sig-seal-overlay) {
  width: 100% !important;
  margin: 0 !important;
}

.contract-capture-host :deep(div[style*='min-height:160']),
.contract-capture-host :deep(div[style*='min-height: 160']) {
  min-height: 0 !important;
}

.contract-capture-host :deep(img) {
  max-width: 100% !important;
  height: auto !important;
}
.hint {
  margin-left: 10px;
  font-size: 12px;
  color: #909399;
}
.muted {
  color: #909399;
  font-size: 13px;
  margin-bottom: 10px;
}
</style>
